import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface TestimonialsProps {
  lang: 'uk' | 'ru';
}

const content = {
  uk: {
    heading: 'Відгуки клієнтів',
    testimonials: [
      {
        text: 'Ірина допомогла мені пережити складний період у житті. Її професіоналізм і чуйність створили атмосферу довіри, в якій я змогла відкритися та знайти рішення. Вдячна за підтримку та новий погляд на себе.',
        author: 'Анна К.',
      },
      {
        text: 'Після кількох сесій я навчився краще розуміти свої емоції та керувати тривогою. Ірина — справжній професіонал, який вміє слухати і ставити правильні запитання. Рекомендую всім, хто шукає підтримку.',
        author: 'Дмитро М.',
      },
      {
        text: 'Робота з Іриною змінила моє ставлення до себе та оточуючих. Я стала впевненішою, навчилася вибудовувати межі та дбати про свої потреби. Дякую за теплу атмосферу та професійний підхід!',
        author: 'Марія В.',
      },
    ],
  },
  ru: {
    heading: 'Отзывы клиентов',
    testimonials: [
      {
        text: 'Ирина помогла мне пережить сложный период в жизни. Её профессионализм и чуткость создали атмосферу доверия, в которой я смогла открыться и найти решение. Благодарна за поддержку и новый взгляд на себя.',
        author: 'Анна К.',
      },
      {
        text: 'После нескольких сессий я научился лучше понимать свои эмоции и управлять тревогой. Ирина — настоящий профессионал, умеющий слушать и задавать правильные вопросы. Рекомендую всем, кто ищет поддержку.',
        author: 'Дмитрий М.',
      },
      {
        text: 'Работа с Ириной изменила моё отношение к себе и окружающим. Я стала увереннее, научилась выстраивать границы и заботиться о своих потребностях. Спасибо за тёплую атмосферу и профессиональный подход!',
        author: 'Мария В.',
      },
    ],
  },
};

export default function Testimonials({ lang }: TestimonialsProps) {
  const t = content[lang];
  const [currentIndex, setCurrentIndex] = useState(0);
  const { ref: headingRef, isVisible: headingVisible } = useScrollAnimation();
  const { ref: cardRef, isVisible: cardVisible } = useScrollAnimation({ rootMargin: '0px 0px -40px 0px' });

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? t.testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === t.testimonials.length - 1 ? 0 : prev + 1));
  };

  return (
    <section id="testimonials" className="py-16 md:py-24 bg-white dark:bg-[#121212]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div
          ref={headingRef}
          className={`text-center mb-10 md:mb-14 transition-all duration-700 ${headingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="text-3xl md:text-5xl font-light text-[#333333] dark:text-[#EEEEEE]">{t.heading}</h2>
        </div>

        <div className="max-w-4xl mx-auto">
          <div
            ref={cardRef}
            className={`bg-white dark:bg-[#1E1E1E] rounded-[20px] md:rounded-[28px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.4)] px-7 py-10 md:px-12 md:py-16 lg:px-20 lg:py-20 relative transition-all duration-700 ${cardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <div className="absolute top-5 left-5 md:top-8 md:left-8 text-6xl md:text-8xl text-[#A8DADC] dark:text-[#3A6A7A] opacity-40 leading-none">"</div>

            <div className="relative z-10">
              <p className="text-base md:text-xl lg:text-2xl text-[#444444] dark:text-[#CCCCCC] leading-relaxed text-center mb-6 md:mb-8">
                {t.testimonials[currentIndex].text}
              </p>
              <div className="w-24 md:w-32 h-px bg-[#E5E5E5] dark:bg-[#333333] mx-auto mb-4 md:mb-6"></div>
              <div className="text-center text-[15px] md:text-[16px] font-semibold text-[#333333] dark:text-[#EEEEEE]">
                — {t.testimonials[currentIndex].author}
              </div>
            </div>

            <div className="absolute bottom-5 right-5 md:bottom-8 md:right-8 text-6xl md:text-8xl text-[#A8DADC] dark:text-[#3A6A7A] opacity-40 leading-none">"</div>
          </div>

          <div className={`flex items-center justify-center gap-4 mt-6 md:mt-8 transition-all duration-700 ${cardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '200ms' }}>
            <button
              onClick={handlePrev}
              className="w-11 h-11 md:w-12 md:h-12 rounded-full border-2 border-[#E5E5E5] dark:border-[#333333] flex items-center justify-center hover:border-[#7EC8E3] dark:hover:border-[#7EC8E3] transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-[#666666] dark:text-[#AAAAAA] text-xl"></i>
            </button>
            <button
              onClick={handleNext}
              className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-[#333333] dark:bg-[#7EC8E3] flex items-center justify-center hover:bg-[#7EC8E3] dark:hover:bg-[#6BB5D0] transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-arrow-right-line text-white text-xl"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
